/**
 * VideoSource. Wraps a <video> element and a THREE.VideoTexture.
 * Handles async load (loadeddata event) and play(). Never touches
 * AudioContext, WebGLRenderer, or PointerEvents.
 */

import * as THREE from 'three';

const VIDEO_LOAD_TIMEOUT_MS = 8_000;

// ─── VideoSource ─────────────────────────────────────────────────────────────

export class VideoSource {
  /** The underlying <video> element. Exposed so tests can dispatch events. */
  readonly video: HTMLVideoElement;

  /**
   * The VideoTexture — available after `ready()` resolves.
   * Null before `loadeddata` fires.
   */
  texture: THREE.VideoTexture | null = null;

  private _readyPromise: Promise<void>;

  constructor(src: string) {
    const video = document.createElement('video');
    video.src = src;
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    // iOS Safari treats muted+playsinline+autoplay as auto-startable (no user
    // gesture needed). Without this, the VideoTexture stays frozen on frame 0
    // because the underlying <video> never starts playing.
    video.autoplay = true;

    this.video = video;

    // Build the ready promise: resolves when loadeddata fires.
    this._readyPromise = new Promise<void>((resolve, reject) => {
      let settled = false;
      const cleanup = () => {
        video.removeEventListener('loadeddata', onLoaded);
        video.removeEventListener('error', onError);
        clearTimeout(timeout);
      };
      const finish = () => {
        if (settled) return;
        settled = true;
        cleanup();
      };
      const onLoaded = () => {
        finish();
        // Lazily create the VideoTexture on first load.
        if (!this.texture) {
          this.texture = new THREE.VideoTexture(video);
        }
        resolve();
      };
      const onError = () => {
        finish();
        const message = video.error?.message || `could not load ${src}`;
        reject(new Error(`VideoSource: ${message}`));
      };
      const timeout = setTimeout(() => {
        finish();
        reject(new Error(`VideoSource: ${src} did not load within ${VIDEO_LOAD_TIMEOUT_MS}ms`));
      }, VIDEO_LOAD_TIMEOUT_MS);
      video.addEventListener('loadeddata', onLoaded);
      video.addEventListener('error', onError);
    });
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Returns a promise that resolves once the video has loaded enough data
   * to begin playback (loadeddata event). texture will be set after this.
   */
  ready(): Promise<void> {
    return this._readyPromise;
  }

  /**
   * Start playback. Returns the native video.play() promise.
   */
  play(): Promise<void> {
    return this.video.play();
  }

  /**
   * Pause and reset video to the start.
   */
  dispose(): void {
    this.video.pause();
    this.video.src = '';
    if (this.texture) {
      this.texture.dispose();
      this.texture = null;
    }
  }
}
